import { map } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';

import { FleetCardsDataService } from '@services/fleet-cards-data';
import { PublicCardApplicationRouteManager } from '@app/services/public-card-application-route-manager';
import { Session } from '@app/services/session';

import { IFleetCardCompany } from '@app/admin/fleet-card/shared/fleet-cards-waitlist/fleet-cards-waitlist.interface';
import { CompanyCardMetaDataStatus } from '@app/admin/fleet-card/shared/definitions';
import { reloadApp } from '@app/lib/utils';

export enum REDIRECTION_ROUTES {
  CARD_APPLICATION_DEFAULT = '/',
  CARD_APPLICATION_REJECTED = '/admin/fleet-cards/card-application/rejected',
  CARD_APPLICATION_COMPLETED = '/admin/fleet-cards/card-application/complete',
  CARD_APPLICATION_ORDER = '/admin/fleet-cards/card-application/order',
  CARD_APPLICATION_ORDER_NEW_ORDER = '/admin/fleet-cards/orders/new',
  VERIFY_EMAIL = '/apply-cards/verify-email',
}

export interface IFleetCardsCardApplicationGuardConfig {
  route: REDIRECTION_ROUTES;
}

@Injectable()
export class FleetCardsCardApplicationGuard {
  constructor(
    private router: Router,
    private fleetCardsDataService: FleetCardsDataService,
    private publicCardApplicationRouteManager: PublicCardApplicationRouteManager,
    private session: Session,
  ) {}

  public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    const isPublicCardApplicationView: boolean = this.publicCardApplicationRouteManager.isPublicCardApplication();
    const session: Session = this.session;

    return this.fleetCardsDataService.getCompanyCardMetadataStream(false)
      .pipe(map((companyMetadata: IFleetCardCompany) => {
        let statusMatches: boolean;

        if (route.data.route === REDIRECTION_ROUTES.CARD_APPLICATION_DEFAULT) {
          if (isPublicCardApplicationView && !companyMetadata) {
            return true;
          } else if ((companyMetadata.status === CompanyCardMetaDataStatus.APPLICATION_SUBMITTED ||
            companyMetadata.status === CompanyCardMetaDataStatus.CREDIT_APPROVED ||
            companyMetadata.status === CompanyCardMetaDataStatus.APPLICATION_DECLINED ||
            (companyMetadata.status === CompanyCardMetaDataStatus.PUBLIC_APP_VERIFY_EMAIL && !!session.getCompanyConnection())) &&
            isPublicCardApplicationView) {
            reloadApp();
            return false;
          } else if (companyMetadata.status === CompanyCardMetaDataStatus.APPLICATION_SUBMITTED ||
            companyMetadata.status === CompanyCardMetaDataStatus.CREDIT_APPROVED ||
            companyMetadata.status === CompanyCardMetaDataStatus.IN_APP_VERIFY_EMAIL ||
             /** If fleet card application status is 'public_application_under_verification' and public_ app is true
              * then redirect the user to application completed page.
              */

            /** Explanation: There is a slight possibility in public card applications that by the time user lands on the cards
             * dashboard, the card application 'public_application_under_verification' status might not have been updated.
             * To cover this issue backend is passing down the public_app parameter as true in the get companies endpoint.
             * In that case, we send the user to application complete page even though the fleet card application
             * status is 'public_application_under_verification'.
             */
            (companyMetadata.status === CompanyCardMetaDataStatus.PUBLIC_APP_VERIFY_EMAIL &&
              companyMetadata?.cardApplication?.publicApp && !!session.getCompanyConnection())) {
            this.router.navigate([REDIRECTION_ROUTES.CARD_APPLICATION_COMPLETED]);
            return false;
          } else if (companyMetadata.status === CompanyCardMetaDataStatus.APPLICATION_DECLINED) {
            this.router.navigate([REDIRECTION_ROUTES.CARD_APPLICATION_REJECTED]);
            return false;
          } else if (
              isPublicCardApplicationView &&
              companyMetadata.status === CompanyCardMetaDataStatus.PUBLIC_APP_VERIFY_EMAIL
            )
          {
            this.router.navigate([REDIRECTION_ROUTES.VERIFY_EMAIL]);
            return false;
          } else if (isPublicCardApplicationView) {
            return true;
          } else {
            statusMatches = true;
          }
        } else if (route.data.route === REDIRECTION_ROUTES.CARD_APPLICATION_COMPLETED) {
          statusMatches = (companyMetadata.status === CompanyCardMetaDataStatus.APPLICATION_SUBMITTED ||
            companyMetadata.status === CompanyCardMetaDataStatus.CREDIT_APPROVED ||
            companyMetadata.status === CompanyCardMetaDataStatus.IN_APP_VERIFY_EMAIL ||
            (companyMetadata.status === CompanyCardMetaDataStatus.PUBLIC_APP_VERIFY_EMAIL &&
              companyMetadata?.cardApplication?.publicApp && !!session.getCompanyConnection()));
        } 
        else if (route.data.route === REDIRECTION_ROUTES.CARD_APPLICATION_ORDER) {
          statusMatches = companyMetadata.status === CompanyCardMetaDataStatus.APPLICATION_SUBMITTED;
        }
        else if (route.data.route === REDIRECTION_ROUTES.CARD_APPLICATION_REJECTED) {
          statusMatches = companyMetadata.status === CompanyCardMetaDataStatus.APPLICATION_DECLINED;
        } else if (route.data.route === REDIRECTION_ROUTES.VERIFY_EMAIL) {
          statusMatches = companyMetadata.status === CompanyCardMetaDataStatus.PUBLIC_APP_VERIFY_EMAIL;
        }

        if (statusMatches) return true;
        else {
          this.router.navigate(['/']);
          return false;
        }
      }));
  }
}